import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, takeUntil, catchError, finalize } from 'rxjs';
import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import { InsuranceService } from '../../../../../core/services/insurance.service';
import { environment } from '../../../../../../environments';
import {
  LucideAngularModule,
  LoaderIcon,
  RefreshCw,
  AlertCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  FileText,
} from 'lucide-angular';
import { InsuranceListComponent } from '../../../components/insurance-list/insurance-list.component';
import { InsuranceDetailsComponent } from '../../../components/insurance-details/insurance-details.component';
import { InsuranceHistoryComponent } from '../../../components/insurance-history/insurance-history.component';
import { InsuranceBeneficiariesComponent } from '../../../components/insurance-beneficiaries/insurance-beneficiaries.component';
import { Country } from '../../../../../core/interfaces/doctor.interface';

interface Insurance {
  id: number;
  policyNumber: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'pending' | 'expired';
}

@Component({
  selector: 'app-insurances-liste',
  standalone: true,
  imports: [CommonModule, BannerComponent, LucideAngularModule],
  templateUrl: './liste.component.html',
})
export class InsurancesListeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isDppTheme = environment.theme.default === 'dpp';
  links = [
    { label: 'Votre passeport', href: '/passport' },
    { label: 'Contrats', href: undefined },
  ];

  // Pagination
  currentPage = 1;
  itemsPerPage = 12;
  totalItems = 0;
  Math = Math; // Make Math available in template

  // Data
  insurances: Insurance[] = [];
  loading = false;
  error: string | null = null;
  searchTerm = '';

  readonly LoaderIcon = LoaderIcon;
  readonly RefreshCw = RefreshCw;
  readonly AlertCircleIcon = AlertCircleIcon;
  readonly ChevronRightIcon = ChevronRightIcon;
  readonly ChevronLeftIcon = ChevronLeftIcon;
  readonly FileText = FileText;
  selectedCountry: Country | null = null;

  menu = [
    { key: 'list', label: 'Liste' },
    { key: 'details', label: 'Détails' },
    { key: 'history', label: 'Historique' },
    { key: 'beneficiaries', label: 'Bénéficiaires' },
  ];
  selectedMenu = this.menu[0];

  setMenu(key: string) {
    const found = this.menu.find((m) => m.key === key);
    if (found) this.selectedMenu = found;
  }

  constructor(
    private router: Router,
    private insuranceService: InsuranceService
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
          // Mettre à jour les liens avec le code du pays
          this.updateLinks();
        } catch {
          this.selectedCountry = null;
        }
      }
    }
    this.loadInsurances();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateLinks(): void {
    if (this.selectedCountry?.code) {
      this.links[0].href = `/${this.selectedCountry.code}/passport`;
    }
  }

  loadInsurances(): void {
    this.loading = true;
    this.error = null;

    this.insuranceService
      .getUserContracts()
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error('Erreur lors du chargement des assurances:', error);
          this.error =
            'Erreur lors du chargement des assurances. Veuillez réessayer.';
          // Retourner un observable vide pour éviter l'erreur
          return [];
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response) => {
          if (response && response.contracts) {
            this.insurances = this.transformApiData(response.contracts);
          } else {
            this.insurances = [];
          }
          this.totalItems = this.insurances.length;
        },
        error: (error) => {
          console.error('Erreur lors du chargement des assurances:', error);
          this.error =
            'Erreur lors du chargement des assurances. Veuillez réessayer.';
          this.insurances = [];
          this.totalItems = 0;
        },
      });
  }

  private transformApiData(apiData: any[]): Insurance[] {
    console.log({ apiData });
    return apiData.map((item, index) => ({
      id: item._id,
      policyNumber: item.contractCustomId,
      name: item.insurer.name || 'Assurance inconnue',
      startDate: this.formatDateForDisplay(
        item.periods[0].periodConfigStartDate
      ),
      endDate: this.formatDateForDisplay(item.periods[0].periodConfigEndDate),
      status: item.status,
      // status: this.determineStatus(
      //   item.status,
      //   item.endDate,
      //   item.expirationDate
      // ),
    }));
  }

  private formatDateForDisplay(dateValue: string): string {
    if (!dateValue) return '';

    // Si la date est déjà au format DD/MM/YYYY, la retourner telle quelle
    if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dateValue;
    }

    // Si c'est une date ISO ou autre format, la convertir
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return dateValue; // Retourner la valeur originale si la conversion échoue
      }
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      console.warn('Erreur lors du formatage de la date:', dateValue, error);
      return dateValue; // Retourner la valeur originale en cas d'erreur
    }
  }

  private determineStatus(
    status: string,
    endDate?: string,
    expirationDate?: string
  ): 'active' | 'pending' | 'expired' {
    if (status) {
      const statusLower = status.toLowerCase();
      if (statusLower.includes('active') || statusLower.includes('actif'))
        return 'active';
      if (statusLower.includes('pending') || statusLower.includes('en_attente'))
        return 'pending';
      if (statusLower.includes('expired') || statusLower.includes('expiré'))
        return 'expired';
    }

    // Déterminer le statut basé sur la date d'expiration
    const expirationDateStr = endDate || expirationDate;
    if (expirationDateStr) {
      const expiration = new Date(expirationDateStr);
      const today = new Date();

      if (expiration < today) {
        return 'expired';
      } else if (
        expiration.getTime() - today.getTime() <
        30 * 24 * 60 * 60 * 1000
      ) {
        // 30 jours
        return 'pending';
      } else {
        return 'active';
      }
    }

    return 'active'; // Par défaut
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.currentPage = 1; // Reset to first page when searching
  }

  get filteredInsurances(): Insurance[] {
    if (!this.searchTerm.trim()) {
      return this.insurances;
    }

    const searchLower = this.searchTerm.toLowerCase();
    return this.insurances.filter(
      (insurance) =>
        insurance.policyNumber.toLowerCase().includes(searchLower) ||
        insurance.name.toLowerCase().includes(searchLower) ||
        insurance.status.toLowerCase().includes(searchLower)
    );
  }

  navigateToDetails(insurance: Insurance): void {
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/insurances`,
      insurance.id,
    ]);
  }

  get paginatedInsurances(): Insurance[] {
    const filtered = this.filteredInsurances;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredInsurances.length / this.itemsPerPage);
  }

  get pages(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 5); // Limiter à 5 pages max pour l'affichage
    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, startPage + maxPages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  refreshData(): void {
    this.loadInsurances();
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Actif':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      case 'pending':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'active':
        return 'Active';
      case 'pending':
        return 'En attente';
      case 'expired':
        return 'Expirée';
      default:
        return status;
    }
  }
}
